/**
 * Investment Return Calculator Page
 * Calculate investment growth with compound interest over time
 */
import { useState, useEffect } from 'react';
import { useCustomTranslation } from '../../hooks';
import { trackPageView } from '../../utils/analytics';
import { Box, Typography, TextField, Button, Card, CardContent } from '@mui/material';
import AmazonRecommendation from '../../components/common/AmazonRecommendation';


interface InvestmentReturnData {
  initial_investment: number;
  monthly_contribution: number;
  annual_return_rate: number;
  years: number;
}

interface InvestmentReturnResults {
  final_value: number;
  total_contributed: number;
  interest_earned: number;
  total_return_percentage: number;
}

export default function InvestmentReturnPage() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';
  const [formData, setFormData] = useState<InvestmentReturnData>({
    initial_investment: 0,
    monthly_contribution: 0,
    annual_return_rate: 7,
    years: 10,
  });
  const [results, setResults] = useState<InvestmentReturnResults | null>(null);

  useEffect(() => {
    trackPageView('/calculators/investment-return', 'Investment Return Calculator');
    document.title = `${t('calculators.investmentReturn.title')} | PayWise365`;
  }, [t]);

  const handleCalculate = () => {
    const monthlyRate = formData.annual_return_rate / 100 / 12;
    const totalMonths = formData.years * 12;
    let balance = formData.initial_investment;

    // Calculate compound interest with monthly contributions
    for (let i = 0; i < totalMonths; i++) {
      balance = balance * (1 + monthlyRate) + formData.monthly_contribution;
    }

    const total_contributed = formData.initial_investment + (formData.monthly_contribution * totalMonths);
    const interest_earned = balance - total_contributed;
    const total_return_percentage = ((balance - formData.initial_investment) / formData.initial_investment) * 100;

    setResults({
      final_value: balance,
      total_contributed,
      interest_earned,
      total_return_percentage,
    });
  };

  return (
    <div className="calculator-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{t('calculators.investmentReturn.title')}</h1>
          <p>{t('calculators.investmentReturn.pageSubtitle')}</p>
        </div>
      </div>
      
      <main className="calculator-content">
        <div className="calculator-container">
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t('form.title')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <TextField
                    fullWidth
                    label="Initial Investment (HKD)"
                    type="number"
                    value={formData.initial_investment || ''}
                    onChange={(e) => setFormData({ ...formData, initial_investment: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Monthly Contribution (HKD)"
                    type="number"
                    value={formData.monthly_contribution || ''}
                    onChange={(e) => setFormData({ ...formData, monthly_contribution: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Annual Return Rate (%)"
                    type="number"
                    value={formData.annual_return_rate}
                    onChange={(e) => setFormData({ ...formData, annual_return_rate: parseFloat(e.target.value) || 0 })}
                    helperText="Historical average: 7-10% for stocks"
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Investment Period (Years)"
                    type="number"
                    value={formData.years}
                    onChange={(e) => setFormData({ ...formData, years: parseInt(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <Button variant="contained" fullWidth onClick={handleCalculate} size="large">
                    Calculate
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {results && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Results
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'success.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Final Investment Value</Typography>
                      <Typography variant="h5">HKD {results.final_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'info.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Total Contributed</Typography>
                      <Typography variant="h5">HKD {results.total_contributed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Interest/Gains Earned</Typography>
                      <Typography variant="h5">HKD {results.interest_earned.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'warning.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Total Return</Typography>
                      <Typography variant="h5">{results.total_return_percentage.toFixed(1)}%</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <div style={{ width: '100%', padding: '30px 20px' }}>
        <AmazonRecommendation isEnglish={isEnglish} />
      </div>
    </div>
  );
}
